<template>
  <mds-modal
    :key="renderKey"
    action-required
    class="scheduler-modal"
    :fullscreen="fullScreen"
    :visible="showSchedulerModal"
    width="600px"
  >
    <scheduler-modal-wrapper
      v-if="!scheduleFeed"
      :show-scheduler-modal="showSchedulerModal"
      :modal-loading="modalLoading"
      :performance-report-parameters="performanceReportParameters"
      :grid-view-parameters="gridViewParameters"
      :save-feed-response="saveFeedResponse"
      :save-feed-status="saveFeedStatus"
      :has-duplicate-name="hasDuplicateName"
      :error-loading-parameters="errorLoadingParameters"
      @get-parameters="getParameters"
      @save="createFeed"
      @close="$emit('close', $event)"
      @hide-feedback-message="hideFeedbackMessage"
    />
    <frequency-modal-wrapper
      v-else
      :modal-loading="modalLoading"
      :save-schedule-response="saveScheduleResponse"
      :save-schedule-status="saveScheduleStatus"
      :feed-id="feedId"
      :has-duplicate-name="hasDuplicateName"
      @createSchedule="createSchedule"
      @close="$emit('close', $event)"
      @hide-feedback-message="hideFeedbackMessage"
    />
  </mds-modal>
</template>

<script>
import MdsModal from '@mds/modal'
import { mapState } from 'vuex'
import isEmpty from 'lodash/isEmpty'
import SchedulerModalWrapper from './SchedulerModalWrapper'
import FrequencyModalWrapper from '@/components/frequencyModal/FrequencyModalWrapper.vue'
import { formatSchedule } from '@/utils/scheduleFormatter'
import {
  getNotebookParameters,
  getSourceName,
  formatInvestmentObject,
} from '@/utils/feedMapper'
import { NOTEBOOK_TITLES, NOTEBOOK_PARAMETER_NAMES } from './config/constants'
import {
  FEED_ACTION_MESSAGE,
  MODAL_LOADED_MESSAGE,
} from '@/components/schedulerModal/config/constants'

export default {
  name: 'SchedulerModal',
  components: {
    MdsModal,
    SchedulerModalWrapper,
    FrequencyModalWrapper,
  },
  data() {
    return {
      performanceReportParameters: [],
      gridViewParameters: [],
      configNotebooksId: [],
      notebook: {},
      modalLoading: true,
      saveFeedResponse: false,
      saveScheduleResponse: false,
      hasDuplicateName: false,
      configNotebookIds: [],
      saveFeedStatus: false,
      saveScheduleStatus: false,
      feedId: undefined,
      scheduleFeed: false,
      fullScreen: false,
      renderKey: true,
      errorLoadingParameters: false,
    }
  },
  async mounted() {
    const { path = '' } = this.$route ?? {}
    this.fullScreen = path.includes('scheduler')
    this.scheduleFeed = this.scheduleFeedView
  },
  props: {
    showSchedulerModal: {
      type: Boolean,
      default: false,
    },
    scheduleFeedView: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState({
      cache: (state) => state.cache.cache,
    }),
    records() {
      return this.cache.feeds ?? []
    },
    editedFeed() {
      return this.cache.editedFeed
    },
    editedSchedule() {
      return this.cache.editedSchedule
    },
    requestFromLens() {
      return this.cache.requestFromLens
    },
  },
  methods: {
    async open(editMode, feedToEdit) {
      // Show loader spinner when not iframed (fullscreen), otherwise load async for iframe performance
      this.modalLoading = true
      this.feedId = feedToEdit

      await this.$store.dispatch('userSettings')

      // We don't need the config notebooks id for schedules
      if (!this.scheduleFeed) {
        this.configNotebooksId = await this.$store.dispatch(
          'getConfigNotebookId',
          NOTEBOOK_TITLES
        )
      }

      if (editMode) {
        // If we are in edit mode, we need to call the endpoint to retrieve the modal parameters.
        if (feedToEdit) {
          await this.$store.dispatch('getSelectedFeed', Number(feedToEdit))
          await this.getParameters(this.editedFeed.investment_object_type)
        }
      }
      this.modalLoading = false
      window.top.postMessage(MODAL_LOADED_MESSAGE, '*')
    },
    refresh() {
      this.renderKey = !this.renderKey
      this.saveFeedResponse = false
    },
    async getParameters(source) {
      const notebookSource = getNotebookParameters(source)

      this.notebook = this.configNotebooksId.find(
        (notebook) => notebook.notebook_name === notebookSource.notebookName
      )

      if (this.notebook && this.notebook?.id) {
        const parameters = await this.$store.dispatch(
          'getNotebookParameters',
          this.notebook.id
        )

        if (!isEmpty(parameters)) {
          this[notebookSource.parameters] = parameters
          this.saveFeedResponse = false
          this.errorLoadingParameters = false
        } else {
          this[notebookSource.parameters] = parameters
          this.errorLoadingParameters = true
          this.saveFeedResponse = true
        }
      }
    },
    async createFeed(modalInfo) {
      const { parameters = {}, scheduleFeed = false } = modalInfo
      this.modalLoading = true

      if (parameters) {
        const requestBody = this.formatRequestBody(parameters)
        const { feed, hasDuplicateName } = await this.createOrUpdateFeed(
          requestBody
        )

        const feedIsCreatedOrUpdated = !!feed

        // If we want to schedule it, let's show the schedule modal
        if (feedIsCreatedOrUpdated && scheduleFeed) {
          await this.$store.dispatch('getSelectedFeed', Number(feed.id))
          this.feedId = feed.id
          this.scheduleFeed = scheduleFeed
          this.saveScheduleResponse = false
        } else {
          this.saveFeedResponse = true
          this.saveFeedStatus = feedIsCreatedOrUpdated
          this.hasDuplicateName = hasDuplicateName

          // We remove the current edited feed from the cache in all cases except for Direct Lens
          // We need to send it to Lens to handle the redirect to feed details action
          if (
            this.saveFeedResponse &&
            this.saveFeedStatus &&
            !this.cache.requestFromLens
          ) {
            this.$store.dispatch('getSelectedFeed', undefined)
          }
          window.top.postMessage(FEED_ACTION_MESSAGE, '*')
        }
        this.modalLoading = false
      }
    },
    async createSchedule(scheduleData) {
      this.modalLoading = true

      let scheduleResponse = {}
      if (!this.editedSchedule) {
        scheduleResponse = await this.$store.dispatch('createSchedule', {
          feedId: this.feedId,
          scheduleInfo: formatSchedule(scheduleData),
        })
      } else {
        scheduleResponse = await this.$store.dispatch('updateSchedule', {
          feedId: this.editedSchedule.feed_id,
          scheduleId: this.editedSchedule.id,
          scheduleInfo: formatSchedule(scheduleData),
        })
      }

      const { schedule, hasDuplicateName } = scheduleResponse

      this.modalLoading = false
      this.saveScheduleStatus = !!schedule
      this.hasDuplicateName = hasDuplicateName
      this.saveScheduleResponse = true

      if (this.saveScheduleResponse && this.saveScheduleStatus) {
        console.log('New feed with schedule!')
        window.top.postMessage(FEED_ACTION_MESSAGE, '*')
      }
    },
    formatRequestBody(modalInfo) {
      const { job_name, ...rest } = modalInfo
      const { investment_object_type } = modalInfo

      const performanceReportProps = this.getPerformanceReportProps(modalInfo)

      return {
        job_name,
        notebook_id: this.notebook.id,
        notebook_parameters: {
          ...rest,
          ...performanceReportProps,
          investment_object_type:
            getSourceName(investment_object_type) ?? investment_object_type,
        },
      }
    },
    getPerformanceReportProps({ investment_object_type, investment_item }) {
      if (
        formatInvestmentObject(investment_object_type) ===
        NOTEBOOK_PARAMETER_NAMES.performance_report
      ) {
        return {
          performance_report: investment_item,
        }
      }
      return {}
    },
    async createOrUpdateFeed(requestBody) {
      if (!this.editedFeed) {
        // If the editedFeed from the store is undefined, let's create a new feed.
        return await this.$store.dispatch('createFeed', requestBody)
      } else {
        // If not, let's call the PUT endpoint to update it.
        return await this.$store.dispatch('editFeed', {
          requestBody,
          feedId: this.editedFeed.feedInfo.id,
        })
      }
    },
    close() {
      this.$store.dispatch('getSelectedFeed', undefined)
      this.saveFeedResponse = false
    },
    hideFeedbackMessage() {
      this.saveScheduleStatus = false
      this.saveFeedStatus = false
      this.hasDuplicateName = false
      this.saveFeedResponse = false
      this.saveScheduleResponse = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@mds/constants';
@import '@mds/typography';

.scheduler-modal {
  &::v-deep > [class^='mds-modal__wrapper'] {
    height: 670px;
    [class^='mds-modal__content'] {
      flex-direction: column;
    }
  }
}
</style>
